/**
 * Applications API Service
 * Handles CRUD operations for application drafts and submissions
 */

import { directus } from "@/lib/directus";
import {
  readItems,
  readItem,
  createItem,
  updateItem,
  deleteItem,
  uploadFiles,
} from "@directus/sdk";
import type { ApplicationFormData, EquipmentType, DocumentType } from "@/lib/application";
import { calculateAssistance } from "@/lib/application";

// API Types - Note: id is number (auto-increment), user_id is UUID string
export interface ApplicationRecord {
  id: number;
  user_id: string;
  status: ApplicationStatus;
  current_step: number;
  
  // Application identifiers
  application_number: string | null;  // Unique 8-digit public ID
  user_seq: number | null;            // Per-user sequential number
  
  // Recipient
  recipient_type: string | null;
  organization_name: string | null;
  edrpou_code: string | null;
  head_full_name: string | null;
  legal_address: string | null;
  actual_address: string | null;
  phone: string | null;
  email: string | null;
  iban: string | null;
  
  // Building
  building_address: string | null;
  floors_count: number | null;
  boiler_room_type: string | null;
  has_tech_passport: boolean;
  co_owners_count: number | null;
  apartments_count: number | null;
  total_area: number | null;
  assistance_category: string | null;
  assistance_amount: number | null;
  
  // Contract
  has_supply_contract: boolean;
  contract_number: string | null;
  contract_date: string | null;
  supplier_name: string | null;
  supplier_edrpou: string | null;
  equipment_description: string | null;
  equipment_cost: number | null;
  delivery_deadline: string | null;
  
  // Obligations
  obligations_accepted: boolean;
  consent_data_processing: boolean;
  consent_terms: boolean;
  
  // Workflow - Submission
  submitted_at: string | null;
  signature_data: string | null;
  signed_at: string | null;
  
  // Workflow - Review
  reviewed_at: string | null;
  reviewed_by: string | null;
  
  // Workflow - Decision
  decision_type: "approved" | "rejected" | null;
  decision_number: string | null;
  decision_date: string | null;
  rejection_reason: string | null;
  rejection_notes: string | null;
  
  // Workflow - Order
  order_number: string | null;
  order_date: string | null;
  
  // Workflow - Payment
  payment_date: string | null;
  payment_amount: number | null;
  payment_received_at: string | null;
  
  // Priority
  priority_score: number | null;
  
  // System
  date_created: string;
  date_updated: string | null;
  
  // Relations (when fetched with fields)
  equipment?: ApplicationEquipmentRecord[];
  documents?: ApplicationDocumentRecord[];
}

export interface ApplicationEquipmentRecord {
  id: number;
  application_id: number;
  equipment_type: EquipmentType;
}

export interface ApplicationDocumentRecord {
  id: number;
  application_id: number;
  document_type: DocumentType;
  file_id: string | null;
  file_name: string | null;
  uploaded_at: string | null;
}

export type ApplicationStatus = 
  | "draft"
  | "submitted"
  | "under_review"
  | "documents_requested"
  | "approved"
  | "rejected"
  | "order_issued"
  | "payment_pending"
  | "payment_sent"
  | "payment_received"
  | "in_progress"
  | "report_pending"
  | "report_submitted"
  | "report_review"
  | "report_approved"
  | "completed"
  | "funds_returned"
  | "cancelled";

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  draft: "Чернетка",
  submitted: "Подано",
  under_review: "На розгляді",
  documents_requested: "Запит документів",
  approved: "Схвалено",
  rejected: "Відхилено",
  order_issued: "Наказ видано",
  payment_pending: "Очікує виплати",
  payment_sent: "Кошти перераховано",
  payment_received: "Кошти отримано",
  in_progress: "Виконується",
  report_pending: "Очікує звіту",
  report_submitted: "Звіт подано",
  report_review: "Звіт на перевірці",
  report_approved: "Звіт затверджено",
  completed: "Завершено",
  funds_returned: "Кошти повернено",
  cancelled: "Скасовано",
};

// Rejection reason codes and labels
export type RejectionReason = 
  | "incomplete_docs"
  | "invalid_data"
  | "ineligible"
  | "duplicate"
  | "fraud_suspected"
  | "building_ineligible"
  | "equipment_ineligible"
  | "budget_exceeded"
  | "deadline_missed"
  | "other";

export const REJECTION_REASON_LABELS: Record<RejectionReason, string> = {
  incomplete_docs: "Неповний пакет документів",
  invalid_data: "Некоректні або недостовірні дані",
  ineligible: "Заявник не відповідає вимогам програми",
  duplicate: "Повторна заявка",
  fraud_suspected: "Підозра на шахрайство",
  building_ineligible: "Будинок не відповідає вимогам",
  equipment_ineligible: "Обладнання не відповідає вимогам",
  budget_exceeded: "Перевищено ліміт фінансування",
  deadline_missed: "Пропущено терміни подання",
  other: "Інша причина",
};

// Helper to get rejection reason label (handles both codes and free-text)
export function getRejectionReasonLabel(reason: string | null): string {
  if (!reason) return "Не вказано";
  return REJECTION_REASON_LABELS[reason as RejectionReason] || reason;
}

/**
 * Get all applications for current user
 */
export interface GetApplicationsParams {
  page?: number;
  limit?: number;
  status?: ApplicationStatus | "all";
  search?: string;
  dateFilter?: "all" | "today" | "week" | "month" | "quarter";
}

export interface GetApplicationsResult {
  data: ApplicationRecord[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export async function getApplications(params: GetApplicationsParams = {}): Promise<GetApplicationsResult> {
  const { page = 1, limit = 9, status = "all", search = "", dateFilter = "all" } = params;
  
  try {
    // Build filter
    const filter: Record<string, unknown> = {};
    
    // Status filter
    if (status !== "all") {
      filter.status = { _eq: status };
    }
    
    // Search filter
    if (search.trim()) {
      filter._or = [
        { building_address: { _icontains: search.trim() } },
        { application_number: { _icontains: search.trim() } },
        { organization_name: { _icontains: search.trim() } },
      ];
    }
    
    // Date filter
    if (dateFilter !== "all") {
      const now = new Date();
      let dateFrom: Date;
      
      switch (dateFilter) {
        case "today":
          dateFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case "week":
          dateFrom = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case "month":
          dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case "quarter":
          dateFrom = new Date(now.getFullYear(), now.getMonth() - 3, 1);
          break;
        default:
          dateFrom = new Date(0);
      }
      
      filter.date_created = { _gte: dateFrom.toISOString() };
    }

    const hasFilter = Object.keys(filter).length > 0;

    // Get total count first
    const countResult = await directus.request<{ id: number }[]>(
      readItems("applications", {
        fields: ["id"],
        ...(hasFilter && { filter }),
        limit: -1,
      })
    );
    const total = countResult?.length || 0;
    
    // Get paginated data
    const result = await directus.request<ApplicationRecord[]>(
      readItems("applications", {
        fields: ["*"],
        ...(hasFilter && { filter }),
        sort: ["-date_updated", "-date_created"],
        limit,
        offset: (page - 1) * limit,
      })
    );
    
    return {
      data: result || [],
      total,
      page,
      limit,
      totalPages: Math.ceil(total / limit),
    };
  } catch (error) {
    console.error("Failed to fetch applications:", error);
    return {
      data: [],
      total: 0,
      page: 1,
      limit,
      totalPages: 0,
    };
  }
}

/**
 * Get single application by ID
 */
export async function getApplication(id: string | number): Promise<ApplicationRecord | null> {
  try {
    const result = await directus.request<ApplicationRecord>(
      readItem("applications", id, {
        fields: ["*"],
      })
    );
    
    if (result) {
      // Fetch related equipment separately (to avoid permission issues with nested fields)
      try {
        const equipment = await directus.request<ApplicationEquipmentRecord[]>(
          readItems("application_equipment", {
            filter: { application_id: { _eq: id } },
            fields: ["*"],
          })
        );
        result.equipment = equipment || [];
      } catch (e) {
        console.warn("Could not fetch equipment:", e);
        result.equipment = [];
      }
      
      // Fetch related documents separately
      try {
        const documents = await directus.request<ApplicationDocumentRecord[]>(
          readItems("application_documents", {
            filter: { application_id: { _eq: id } },
            fields: ["*"],
          })
        );
        result.documents = documents || [];
      } catch (e) {
        console.warn("Could not fetch documents:", e);
        result.documents = [];
      }
    }
    
    return result || null;
  } catch (error) {
    console.error("Failed to fetch application:", error);
    return null;
  }
}

/**
 * Generate application number in format: EDRPOU-XX
 * Example: 44556677-01, 44556677-02
 */
function generateApplicationNumber(edrpou: string, userSeq: number): string {
  const seqPadded = userSeq.toString().padStart(2, "0");
  return `${edrpou}-${seqPadded}`;
}

/**
 * Get next user sequence number based on EDRPOU
 * This ensures sequential numbering per organization
 */
async function getNextUserSeq(edrpou: string): Promise<number> {
  try {
    const existing = await directus.request<{ user_seq: number | null }[]>(
      readItems("applications", {
        filter: { edrpou_code: { _eq: edrpou } },
        fields: ["user_seq"],
        sort: ["-user_seq"],
        limit: 1,
      })
    );
    
    const maxSeq = existing?.[0]?.user_seq || 0;
    return maxSeq + 1;
  } catch (error) {
    console.error("Failed to get user seq:", error);
    return 1;
  }
}

/**
 * Create new draft application
 * @param userId - User ID from idgov_users
 * @param edrpou - Organization EDRPOU code (for numbering and folders)
 * @param data - Initial form data
 */
export async function createApplication(
  userId: string,
  edrpou: string,
  data?: Partial<ApplicationFormData>
): Promise<ApplicationRecord | null> {
  try {
    // Generate unique identifiers based on EDRPOU
    const userSeq = await getNextUserSeq(edrpou);
    const applicationNumber = generateApplicationNumber(edrpou, userSeq);
    
    const payload = mapFormDataToRecord(data);
    const result = await directus.request<ApplicationRecord>(
      createItem("applications", {
        user_id: userId,
        status: "draft",
        current_step: 1,
        application_number: applicationNumber,
        user_seq: userSeq,
        edrpou_code: edrpou, // Store EDRPOU for future sequence lookups
        ...payload,
      })
    );
    return result || null;
  } catch (error) {
    console.error("Failed to create application:", error);
    return null;
  }
}

/**
 * Update application (save draft)
 */
export async function updateApplication(
  id: string | number,
  data: Partial<ApplicationFormData>,
  currentStep?: number
): Promise<ApplicationRecord | null> {
  try {
    const payload = mapFormDataToRecord(data);
    if (currentStep !== undefined) {
      (payload as Record<string, unknown>).current_step = currentStep;
    }
    
    const result = await directus.request<ApplicationRecord>(
      updateItem("applications", id, payload)
    );
    return result || null;
  } catch (error) {
    console.error("Failed to update application:", error);
    return null;
  }
}

/**
 * Save equipment types for application
 */
export async function saveEquipmentTypes(
  applicationId: string | number,
  equipmentTypes: EquipmentType[]
): Promise<boolean> {
  try {
    // First, get existing equipment to delete
    const existing = await directus.request<ApplicationEquipmentRecord[]>(
      readItems("application_equipment", {
        filter: { application_id: { _eq: applicationId } },
      })
    );
    
    // Delete existing equipment
    if (existing && existing.length > 0) {
      for (const eq of existing) {
        await directus.request(deleteItem("application_equipment", eq.id));
      }
    }
    
    // Insert new equipment
    if (equipmentTypes.length > 0) {
      for (const type of equipmentTypes) {
        await directus.request(
          createItem("application_equipment", {
            application_id: applicationId,
            equipment_type: type,
          })
        );
      }
    }
    
    return true;
  } catch (error) {
    console.error("Failed to save equipment types:", error);
    return false;
  }
}

/**
 * Save document record (metadata only, file uploaded separately)
 */
export async function saveDocument(
  applicationId: string | number,
  documentType: DocumentType,
  fileId: string | null,
  fileName: string | null
): Promise<ApplicationDocumentRecord | null> {
  try {
    // Check if document already exists
    const existing = await directus.request<ApplicationDocumentRecord[]>(
      readItems("application_documents", {
        filter: {
          application_id: { _eq: applicationId },
          document_type: { _eq: documentType },
        },
      })
    );
    
    const existingDoc = existing?.[0];
    if (existingDoc) {
      // Update existing
      const docId = existingDoc.id;
      const result = await directus.request<ApplicationDocumentRecord>(
        updateItem("application_documents", docId, {
          file_id: fileId,
          file_name: fileName,
        })
      );
      return result || null;
    } else {
      // Create new
      const result = await directus.request<ApplicationDocumentRecord>(
        createItem("application_documents", {
          application_id: applicationId,
          document_type: documentType,
          file_id: fileId,
          file_name: fileName,
        })
      );
      return result || null;
    }
  } catch (error) {
    console.error("Failed to save document:", error);
    return null;
  }
}

/**
 * Delete document
 */
export async function deleteDocument(documentId: string | number): Promise<boolean> {
  try {
    await directus.request(deleteItem("application_documents", documentId));
    return true;
  } catch (error) {
    console.error("Failed to delete document:", error);
    return false;
  }
}

/**
 * Get or create a folder in Directus
 */
async function getOrCreateFolder(
  name: string, 
  parentId: string | null = null
): Promise<string | null> {
  try {
    // Try to find existing folder
    const existing = await directus.request<{ id: string; name: string }[]>(
      readItems("directus_folders", {
        filter: {
          name: { _eq: name },
          parent: parentId ? { _eq: parentId } : { _null: true },
        },
        limit: 1,
      })
    );
    
    const firstFolder = existing?.[0];
    if (firstFolder) {
      return firstFolder.id;
    }
    
    // Create new folder
    const newFolder = await directus.request<{ id: string }>(
      createItem("directus_folders", {
        name,
        parent: parentId,
      })
    );
    
    return newFolder?.id || null;
  } catch (error) {
    console.error("Failed to get/create folder:", error);
    return null;
  }
}

/**
 * Upload file to Directus with organized folder structure
 * Structure: applications/{EDRPOU}/{application_number}/
 * Example: applications/44556677/44556677-01/
 */
export async function uploadFile(
  file: File, 
  edrpou?: string,
  applicationNumber?: string
): Promise<string | null> {
  try {
    let folderId: string | null = null;
    
    // Create folder structure if we have edrpou and applicationNumber
    if (edrpou && applicationNumber) {
      // 1. Get/create "applications" root folder
      const rootFolderId = await getOrCreateFolder("applications");
      
      if (rootFolderId) {
        // 2. Get/create EDRPOU folder
        const edrpouFolderId = await getOrCreateFolder(edrpou, rootFolderId);
        
        if (edrpouFolderId) {
          // 3. Get/create application number folder (e.g., "44556677-01")
          folderId = await getOrCreateFolder(applicationNumber, edrpouFolderId);
        }
      }
    }
    
    const formData = new FormData();
    formData.append("file", file);
    if (folderId) {
      formData.append("folder", folderId);
    }
    
    const result = await directus.request(uploadFiles(formData));
    
    return result?.id || null;
  } catch (error) {
    console.error("Failed to upload file:", error);
    return null;
  }
}

/**
 * Upload file for report with organized folder structure
 * Structure: applications/{EDRPOU}/{application_number}/report/
 * Example: applications/44556677/44556677-01/report/
 */
export async function uploadReportFile(
  file: File, 
  edrpou?: string,
  applicationNumber?: string
): Promise<string | null> {
  try {
    let folderId: string | null = null;
    
    // Create folder structure if we have edrpou and applicationNumber
    if (edrpou && applicationNumber) {
      // 1. Get/create "applications" root folder
      const rootFolderId = await getOrCreateFolder("applications");
      
      if (rootFolderId) {
        // 2. Get/create EDRPOU folder
        const edrpouFolderId = await getOrCreateFolder(edrpou, rootFolderId);
        
        if (edrpouFolderId) {
          // 3. Get/create application number folder (e.g., "44556677-01")
          const appFolderId = await getOrCreateFolder(applicationNumber, edrpouFolderId);
          
          if (appFolderId) {
            // 4. Get/create "report" subfolder
            folderId = await getOrCreateFolder("report", appFolderId);
          }
        }
      }
    }
    
    const formData = new FormData();
    formData.append("file", file);
    if (folderId) {
      formData.append("folder", folderId);
    }
    
    const result = await directus.request(uploadFiles(formData));
    
    return result?.id || null;
  } catch (error) {
    console.error("Failed to upload report file:", error);
    return null;
  }
}

/**
 * Submit application (change status from draft to submitted)
 */
export async function submitApplication(id: string | number): Promise<ApplicationRecord | null> {
  try {
    const result = await directus.request<ApplicationRecord>(
      updateItem("applications", id, {
        status: "submitted",
        submitted_at: new Date().toISOString(),
      })
    );
    return result || null;
  } catch (error) {
    console.error("Failed to submit application:", error);
    return null;
  }
}

/**
 * Delete draft application
 */
export async function deleteApplication(id: string | number): Promise<boolean> {
  try {
    await directus.request(deleteItem("applications", id));
    return true;
  } catch (error) {
    console.error("Failed to delete application:", error);
    return false;
  }
}

/**
 * Get user's draft applications
 */
export async function getDraftApplications(): Promise<ApplicationRecord[]> {
  try {
    const result = await directus.request<ApplicationRecord[]>(
      readItems("applications", {
        filter: { status: { _eq: "draft" } },
        fields: ["id", "organization_name", "current_step", "date_created", "date_updated"],
        sort: ["-date_updated"],
      })
    );
    return result || [];
  } catch (error) {
    console.error("Failed to fetch draft applications:", error);
    return [];
  }
}

// Helper: Map form data to API record format
function mapFormDataToRecord(data?: Partial<ApplicationFormData>): Partial<ApplicationRecord> {
  if (!data) return {};
  
  const record: Partial<ApplicationRecord> = {};
  
  // Recipient
  if (data.recipient) {
    const r = data.recipient;
    record.recipient_type = r.recipient_type;
    record.organization_name = r.organization_name || null;
    record.edrpou_code = r.edrpou_code || null;
    record.head_full_name = r.head_full_name || null;
    record.legal_address = r.legal_address || null;
    record.actual_address = r.actual_address || null;
    record.phone = r.phone || null;
    record.email = r.email || null;
    record.iban = r.iban || null;
  }
  
  // Building
  if (data.building) {
    const b = data.building;
    record.building_address = b.building_address || null;
    record.floors_count = b.floors_count;
    record.boiler_room_type = b.boiler_room_type;
    record.has_tech_passport = b.has_tech_passport;
    record.co_owners_count = b.co_owners_count;
    record.apartments_count = b.apartments_count;
    record.total_area = b.total_area;
    
    // Auto-calculate assistance
    if (b.floors_count) {
      const assistance = calculateAssistance(b.floors_count, b.boiler_room_type);
      record.assistance_category = assistance.category;
      record.assistance_amount = assistance.amount;
    }
  }
  
  // Equipment
  if (data.equipment) {
    const e = data.equipment;
    record.has_supply_contract = e.has_supply_contract;
    record.contract_number = e.contract_number || null;
    record.contract_date = e.contract_date || null;
    record.supplier_name = e.supplier_name || null;
    record.supplier_edrpou = e.supplier_edrpou || null;
    record.equipment_description = e.equipment_description || null;
    record.equipment_cost = e.equipment_cost;
    record.delivery_deadline = e.delivery_deadline || null;
  }
  
  // Obligations
  if (data.obligations) {
    const o = data.obligations;
    record.obligations_accepted = o.obligations_accepted;
    record.consent_data_processing = o.consent_data_processing;
    record.consent_terms = o.consent_terms;
  }
  
  return record;
}

/**
 * Map API record to form data format
 */
export function mapRecordToFormData(record: ApplicationRecord): ApplicationFormData {
  return {
    recipient: {
      recipient_type: record.recipient_type as "osbb" | "manager" | "cooperative" | null,
      organization_name: record.organization_name || "",
      edrpou_code: record.edrpou_code || "",
      head_full_name: record.head_full_name || "",
      legal_address: record.legal_address || "",
      actual_address: record.actual_address || "",
      phone: record.phone || "+380",
      email: record.email || "",
      iban: record.iban || "",
    },
    building: {
      building_address: record.building_address || "",
      floors_count: record.floors_count,
      boiler_room_type: record.boiler_room_type as import("@/lib/application/types").BoilerRoomType,
      has_tech_passport: record.has_tech_passport,
      co_owners_count: record.co_owners_count,
      apartments_count: record.apartments_count,
      total_area: record.total_area,
    },
    equipment: {
      equipment_types: record.equipment?.map((e) => e.equipment_type) || [],
      has_supply_contract: record.has_supply_contract,
      contract_number: record.contract_number || "",
      contract_date: record.contract_date || "",
      supplier_name: record.supplier_name || "",
      supplier_edrpou: record.supplier_edrpou || "",
      equipment_description: record.equipment_description || "",
      equipment_cost: record.equipment_cost,
      delivery_deadline: record.delivery_deadline || "",
    },
    documents: record.documents?.map((d) => ({
      id: d.document_type,
      type: d.document_type,
      file: null,
      name: d.file_name || "",
      uploaded: !!d.file_id,
    })) || [],
    obligations: {
      obligations_accepted: record.obligations_accepted,
      consent_data_processing: record.consent_data_processing,
      consent_terms: record.consent_terms,
    },
  };
}

// ============================================================================
// STATUS HISTORY
// ============================================================================

export interface StatusHistoryRecord {
  id: number;
  application_id: number;
  old_status: ApplicationStatus | "new" | null;
  new_status: ApplicationStatus;
  changed_by: string | null;
  changed_at: string;
  notes: string | null;
  ip_address: string | null;
}

/**
 * Record a status change in the history
 */
export async function recordStatusChange(
  applicationId: number,
  oldStatus: ApplicationStatus | "new" | null,
  newStatus: ApplicationStatus,
  userId?: string,
  notes?: string
): Promise<StatusHistoryRecord | null> {
  try {
    const result = await directus.request<StatusHistoryRecord>(
      createItem("application_status_history", {
        application_id: applicationId,
        old_status: oldStatus,
        new_status: newStatus,
        changed_by: userId || null,
        notes: notes || null,
      })
    );
    return result || null;
  } catch (error) {
    console.error("Failed to record status change:", error);
    return null;
  }
}

/**
 * Get status history for an application
 */
export async function getStatusHistory(applicationId: number | string): Promise<StatusHistoryRecord[]> {
  try {
    const result = await directus.request<StatusHistoryRecord[]>(
      readItems("application_status_history", {
        filter: { application_id: { _eq: applicationId } },
        sort: ["-changed_at"],
        fields: ["*"],
      })
    );
    return result || [];
  } catch (error) {
    console.error("Failed to fetch status history:", error);
    return [];
  }
}

// ============================================================================
// DOCUMENT REQUESTS
// ============================================================================

export type DocumentRequestStatus = "pending" | "fulfilled" | "overdue" | "cancelled";

export interface DocumentRequestRecord {
  id: number;
  application_id: number;
  requested_by: string;
  requested_at: string;
  document_type: string;
  description: string;
  deadline: string | null;
  status: DocumentRequestStatus;
  fulfilled_at: string | null;
  fulfilled_document_id: string | null;  // UUID reference to application_documents
  request_phase: "application" | "report";  // Phase: application or report
  report_id: number | null;  // If report phase, the report this request belongs to
}

export const DOCUMENT_REQUEST_STATUS_LABELS: Record<DocumentRequestStatus, string> = {
  pending: "Очікує",
  fulfilled: "Виконано",
  overdue: "Прострочено",
  cancelled: "Скасовано",
};

/**
 * Create a document request for application phase
 */
export async function createDocumentRequest(
  applicationId: number,
  requestedBy: string,
  documentType: string,
  description: string,
  deadline?: string
): Promise<DocumentRequestRecord | null> {
  try {
    const result = await directus.request<DocumentRequestRecord>(
      createItem("document_requests", {
        application_id: applicationId,
        requested_by: requestedBy,
        document_type: documentType,
        description,
        deadline: deadline || null,
        status: "pending",
        request_phase: "application",
        report_id: null,
      })
    );
    return result || null;
  } catch (error) {
    console.error("Failed to create document request:", error);
    return null;
  }
}

/**
 * Create a document request for report phase
 */
export async function createReportDocumentRequest(
  applicationId: number,
  reportId: number,
  requestedBy: string,
  documentType: string,
  description: string,
  deadline?: string
): Promise<DocumentRequestRecord | null> {
  try {
    const result = await directus.request<DocumentRequestRecord>(
      createItem("document_requests", {
        application_id: applicationId,
        report_id: reportId,
        requested_by: requestedBy,
        document_type: documentType,
        description,
        deadline: deadline || null,
        status: "pending",
        request_phase: "report",
      })
    );
    return result || null;
  } catch (error) {
    console.error("Failed to create report document request:", error);
    return null;
  }
}

/**
 * Get document requests for an application
 * @param applicationId - The application ID
 * @param phase - Optional filter by phase ("application" or "report"). If not provided, returns all.
 */
export async function getDocumentRequests(
  applicationId: number | string, 
  phase?: "application" | "report"
): Promise<DocumentRequestRecord[]> {
  try {
    const filter: Record<string, unknown> = { application_id: { _eq: applicationId } };
    
    // Add phase filter if specified
    if (phase) {
      filter.request_phase = { _eq: phase };
    }
    
    const result = await directus.request<DocumentRequestRecord[]>(
      readItems("document_requests", {
        filter,
        sort: ["-requested_at"],
        fields: ["*"],
      })
    );
    return result || [];
  } catch (error) {
    console.error("Failed to fetch document requests:", error);
    return [];
  }
}

/**
 * Get document requests for a report
 * @param reportId - The report ID
 */
export async function getReportDocumentRequests(reportId: number | string): Promise<DocumentRequestRecord[]> {
  try {
    const result = await directus.request<DocumentRequestRecord[]>(
      readItems("document_requests", {
        filter: { 
          report_id: { _eq: reportId },
          request_phase: { _eq: "report" }
        },
        sort: ["-requested_at"],
        fields: ["*"],
      })
    );
    return result || [];
  } catch (error) {
    console.error("Failed to fetch report document requests:", error);
    return [];
  }
}

/**
 * Fulfill a document request
 */
export async function fulfillDocumentRequest(
  requestId: number,
  documentId: string
): Promise<DocumentRequestRecord | null> {
  try {
    const result = await directus.request<DocumentRequestRecord>(
      updateItem("document_requests", requestId, {
        status: "fulfilled",
        fulfilled_at: new Date().toISOString(),
        fulfilled_document_id: documentId,
      })
    );
    return result || null;
  } catch (error) {
    console.error("Failed to fulfill document request:", error);
    return null;
  }
}

/**
 * Cancel a document request
 */
export async function cancelDocumentRequest(requestId: number): Promise<boolean> {
  try {
    await directus.request(
      updateItem("document_requests", requestId, {
        status: "cancelled",
      })
    );
    return true;
  } catch (error) {
    console.error("Failed to cancel document request:", error);
    return false;
  }
}

// ============================================================================
// AUDIT LOG
// ============================================================================

export type AuditAction = 
  | "create" 
  | "update" 
  | "delete" 
  | "read" 
  | "submit" 
  | "sign" 
  | "upload" 
  | "delete_file" 
  | "status_change" 
  | "request_docs";

export type AuditEntityType = 
  | "application" 
  | "document" 
  | "equipment" 
  | "document_request" 
  | "user" 
  | "report";

export interface AuditLogRecord {
  id: number;
  timestamp: string;
  user_id: string | null;
  action: AuditAction;
  entity_type: AuditEntityType;
  entity_id: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  description: string | null;
}

/**
 * Log an audit event
 */
export async function logAuditEvent(
  action: AuditAction,
  entityType: AuditEntityType,
  entityId: string | number,
  userId?: string,
  description?: string,
  oldValues?: Record<string, unknown>,
  newValues?: Record<string, unknown>
): Promise<AuditLogRecord | null> {
  try {
    const result = await directus.request<AuditLogRecord>(
      createItem("audit_log", {
        action,
        entity_type: entityType,
        entity_id: String(entityId),
        user_id: userId || null,
        description: description || null,
        old_values: oldValues || null,
        new_values: newValues || null,
      })
    );
    return result || null;
  } catch (error) {
    console.error("Failed to log audit event:", error);
    return null;
  }
}

/**
 * Get audit logs for an entity
 */
export async function getAuditLogs(
  entityType: AuditEntityType,
  entityId: string | number
): Promise<AuditLogRecord[]> {
  try {
    const result = await directus.request<AuditLogRecord[]>(
      readItems("audit_log", {
        filter: {
          entity_type: { _eq: entityType },
          entity_id: { _eq: String(entityId) },
        },
        sort: ["-timestamp"],
        fields: ["*"],
      })
    );
    return result || [];
  } catch (error) {
    console.error("Failed to fetch audit logs:", error);
    return [];
  }
}

/**
 * Get all audit logs for an application (including related entities)
 */
export async function getApplicationAuditLogs(applicationId: number | string): Promise<AuditLogRecord[]> {
  try {
    const result = await directus.request<AuditLogRecord[]>(
      readItems("audit_log", {
        filter: {
          _or: [
            { entity_type: { _eq: "application" }, entity_id: { _eq: String(applicationId) } },
            // For document and equipment, we check description which contains application ID
          ],
        },
        sort: ["-timestamp"],
        fields: ["*"],
        limit: 100,
      })
    );
    return result || [];
  } catch (error) {
    console.error("Failed to fetch application audit logs:", error);
    return [];
  }
}

// ============================================================================
// ENHANCED APPLICATION FUNCTIONS WITH LOGGING
// ============================================================================

/**
 * Submit application with full logging
 */
export async function submitApplicationWithLog(
  id: string | number,
  userId?: string
): Promise<ApplicationRecord | null> {
  try {
    // Get current application to know the old status
    const current = await getApplication(id);
    if (!current) {
      console.error("Application not found for submission");
      return null;
    }

    // Update status
    const result = await directus.request<ApplicationRecord>(
      updateItem("applications", id, {
        status: "submitted",
        submitted_at: new Date().toISOString(),
      })
    );

    if (result) {
      // Record status change
      await recordStatusChange(
        result.id,
        current.status,
        "submitted",
        userId,
        "Заявку подано на розгляд"
      );

      // Log audit event
      await logAuditEvent(
        "submit",
        "application",
        result.id,
        userId,
        `Заявку №${result.application_number} подано на розгляд`
      );
    }

    return result || null;
  } catch (error) {
    console.error("Failed to submit application:", error);
    return null;
  }
}

/**
 * Change application status with full logging
 */
export async function changeApplicationStatus(
  id: string | number,
  newStatus: ApplicationStatus,
  userId?: string,
  notes?: string,
  additionalData?: Record<string, unknown>
): Promise<ApplicationRecord | null> {
  try {
    // Get current application
    const current = await getApplication(id);
    if (!current) {
      console.error("Application not found for status change");
      return null;
    }

    const oldStatus = current.status;

    // Update status with any additional data
    const updateData: Record<string, unknown> = {
      status: newStatus,
      ...additionalData,
    };

    const result = await directus.request<ApplicationRecord>(
      updateItem("applications", id, updateData)
    );

    if (result) {
      // Record status change
      await recordStatusChange(
        result.id,
        oldStatus,
        newStatus,
        userId,
        notes
      );

      // Log audit event
      await logAuditEvent(
        "status_change",
        "application",
        result.id,
        userId,
        `Статус заявки змінено: ${APPLICATION_STATUS_LABELS[oldStatus]} → ${APPLICATION_STATUS_LABELS[newStatus]}${notes ? `. ${notes}` : ""}`,
        { status: oldStatus },
        { status: newStatus, ...additionalData }
      );
    }

    return result || null;
  } catch (error) {
    console.error("Failed to change application status:", error);
    return null;
  }
}

// ============================================================================
// USER SELF-CANCELLATION
// ============================================================================

/**
 * Statuses where user can cancel their own application
 * - Before financial commitment (before approved status)
 */
export const CANCELLABLE_STATUSES: ApplicationStatus[] = [
  "draft",
  "submitted", 
  "under_review",
  "documents_requested",
];

/**
 * Check if an application can be cancelled by the user
 */
export function canUserCancelApplication(status: ApplicationStatus): boolean {
  return CANCELLABLE_STATUSES.includes(status);
}

/**
 * Get reason why application cannot be cancelled
 */
export function getCancellationBlockedReason(status: ApplicationStatus): string | null {
  if (canUserCancelApplication(status)) {
    return null;
  }
  
  switch (status) {
    case "approved":
      return "Заявку вже схвалено. Скасування неможливе після прийняття рішення.";
    case "rejected":
      return "Заявку вже відхилено.";
    case "order_issued":
    case "payment_pending":
    case "payment_sent":
    case "payment_received":
      return "Кошти вже в процесі перерахування. Скасування неможливе.";
    case "in_progress":
    case "report_pending":
    case "report_submitted":
    case "report_review":
    case "report_approved":
      return "Проєкт в процесі виконання. Скасування неможливе.";
    case "completed":
      return "Заявку успішно завершено.";
    case "funds_returned":
      return "Кошти вже повернено.";
    case "cancelled":
      return "Заявку вже скасовано.";
    default:
      return "Скасування неможливе для поточного статусу.";
  }
}

/**
 * Cancel application by user
 * Only allowed in early stages before financial commitment
 */
export async function cancelApplication(
  id: string | number,
  userId?: string,
  reason?: string
): Promise<{ success: boolean; error?: string; application?: ApplicationRecord }> {
  try {
    // Get current application
    const current = await getApplication(id);
    if (!current) {
      return { success: false, error: "Заявку не знайдено" };
    }

    // Check if cancellation is allowed
    if (!canUserCancelApplication(current.status)) {
      const blockedReason = getCancellationBlockedReason(current.status);
      return { success: false, error: blockedReason || "Скасування неможливе" };
    }

    const oldStatus = current.status;
    const cancellationNote = reason || "Заявку скасовано за ініціативою заявника";

    // Update status to cancelled
    const result = await directus.request<ApplicationRecord>(
      updateItem("applications", id, {
        status: "cancelled",
      })
    );

    if (result) {
      // Record status change
      await recordStatusChange(
        result.id,
        oldStatus,
        "cancelled",
        userId,
        cancellationNote
      );

      // Log audit event
      await logAuditEvent(
        "status_change",
        "application",
        result.id,
        userId,
        `Заявку №${result.application_number} скасовано. ${cancellationNote}`,
        { status: oldStatus },
        { status: "cancelled" }
      );

      return { success: true, application: result };
    }

    return { success: false, error: "Помилка при скасуванні заявки" };
  } catch (error) {
    console.error("Failed to cancel application:", error);
    return { success: false, error: "Помилка сервера при скасуванні заявки" };
  }
}

/**
 * Create application with logging
 */
export async function createApplicationWithLog(
  userId: string,
  edrpou: string,
  data?: Partial<ApplicationFormData>
): Promise<ApplicationRecord | null> {
  const result = await createApplication(userId, edrpou, data);

  if (result) {
    // Record initial status
    await recordStatusChange(
      result.id,
      "new",
      "draft",
      userId,
      "Створено нову заявку"
    );

    // Log audit event
    await logAuditEvent(
      "create",
      "application",
      result.id,
      userId,
      `Створено нову заявку №${result.application_number}`
    );
  }

  return result;
}

/**
 * Request additional documents with logging
 */
export async function requestDocumentsWithLog(
  applicationId: number,
  requestedBy: string,
  documentType: string,
  description: string,
  deadline?: string
): Promise<DocumentRequestRecord | null> {
  // Get current application
  const application = await getApplication(applicationId);
  if (!application) {
    console.error("Application not found for document request");
    return null;
  }

  // Create document request
  const request = await createDocumentRequest(
    applicationId,
    requestedBy,
    documentType,
    description,
    deadline
  );

  if (request) {
    // Change application status if not already in documents_requested
    if (application.status !== "documents_requested") {
      await changeApplicationStatus(
        applicationId,
        "documents_requested",
        requestedBy,
        `Запит документів: ${documentType}`
      );
    }

    // Log audit event
    await logAuditEvent(
      "request_docs",
      "application",
      applicationId,
      requestedBy,
      `Запит документів: ${documentType}. ${description}`
    );
  }

  return request;
}

// ============================================================================
// Reports API
// ============================================================================

export type ReportStatus = "draft" | "submitted" | "review" | "approved" | "rejected";

export const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  draft: "Чернетка",
  submitted: "Подано",
  review: "На перевірці",
  approved: "Затверджено",
  rejected: "Відхилено",
};

export interface ReportRecord {
  id: number;
  application_id: number;
  status: ReportStatus;
  amount_received: number | null;
  amount_used: number | null;
  own_funds_used: number | null;
  unused_funds: number | null;
  equipment_installed: boolean;
  equipment_tested: boolean;
  installation_date: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  rejection_reason: string | null;
  notes: string | null;
  date_created: string;
  date_updated: string | null;
  // Nested
  equipment?: ReportEquipmentRecord[];
  documents?: ReportDocumentRecord[];
}

export interface ReportEquipmentRecord {
  id: number;
  report_id: number;
  item_number: number | null;
  name: string;
  serial_number: string | null;
  quantity: number;
  price_per_unit: number | null;
  price_with_vat: number | null;
  photo_id: string | null; // UUID reference to directus_files
}

export type ReportDocumentType = 
  | "report_approval_decision" 
  | "report_bank_statement" 
  | "report_invoices" 
  | "report_photos";

export const REPORT_DOCUMENT_LABELS: Record<ReportDocumentType, string> = {
  report_approval_decision: "Рішення зборів про затвердження звіту",
  report_bank_statement: "Виписка з банківського рахунку",
  report_invoices: "Накладні / акти приймання-передавання",
  report_photos: "Фото/відео обладнання",
};

export interface ReportDocumentRecord {
  id: number;
  report_id: number;
  document_type: ReportDocumentType;
  file_id: string | null;
  file_name: string | null;
  uploaded_at: string | null;
}

/**
 * Get report for an application
 */
export async function getApplicationReport(applicationId: number | string): Promise<ReportRecord | null> {
  try {
    const reports = await directus.request(
      readItems("reports" as never, {
        filter: {
          application_id: { _eq: Number(applicationId) },
        },
        fields: ["*"],
        limit: 1,
      })
    );

    if (!reports || reports.length === 0) {
      return null;
    }

    const report = reports[0] as unknown as ReportRecord;

    // Load equipment
    const equipment = await directus.request(
      readItems("report_equipment" as never, {
        filter: { report_id: { _eq: report.id } },
        fields: ["*"],
        sort: ["item_number"],
      })
    );
    report.equipment = equipment as unknown as ReportEquipmentRecord[];

    // Load documents
    const documents = await directus.request(
      readItems("report_documents" as never, {
        filter: { report_id: { _eq: report.id } },
        fields: ["*"],
      })
    );
    report.documents = documents as unknown as ReportDocumentRecord[];

    return report;
  } catch (error) {
    console.error("Error fetching report:", error);
    return null;
  }
}
